import * as THREE from 'three';
import type { CharacterConfig } from '../data/characters';

export interface PlayerParts {
  root: THREE.Group;
  body: THREE.Mesh;
  head: THREE.Mesh;
  hair: THREE.Mesh;
  leftArm: THREE.Mesh;
  rightArm: THREE.Mesh;
  leftLeg: THREE.Mesh;
  rightLeg: THREE.Mesh;
  leftEar?: THREE.Mesh;
  rightEar?: THREE.Mesh;
  wings?: { left: THREE.Mesh; right: THREE.Mesh; group: THREE.Group };
}

function mat(color: string, rough = 0.85): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness: rough });
}

export class Player {
  readonly parts: PlayerParts;
  readonly root = new THREE.Group();
  private walkTime = 0;
  private flapTime = 0;

  constructor(config: CharacterConfig) {
    // Body
    const bodyMat = mat(config.outfit);
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 0.6, 4, 12), bodyMat);
    body.position.y = 0.7;
    body.castShadow = true;
    this.root.add(body);

    // Head
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.28, 20, 16), mat(config.skin));
    head.position.y = 1.3;
    head.castShadow = true;
    this.root.add(head);

    // Eyes (static, to face forward +Z)
    const eyeMat = new THREE.MeshStandardMaterial({ color: '#2a1c3c', roughness: 0.3 });
    for (const x of [-0.09, 0.09]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 10, 8), eyeMat);
      eye.position.set(x, 1.32, 0.25);
      this.root.add(eye);
    }
    // Cheeks
    const cheekMat = new THREE.MeshStandardMaterial({ color: '#ffb8c8', roughness: 0.8, transparent: true, opacity: 0.7 });
    for (const x of [-0.13, 0.13]) {
      const c = new THREE.Mesh(new THREE.SphereGeometry(0.04, 10, 8), cheekMat);
      c.position.set(x, 1.25, 0.22);
      this.root.add(c);
    }

    // Hair cap
    const hair = new THREE.Mesh(
      new THREE.SphereGeometry(0.3, 20, 16, 0, Math.PI * 2, 0, Math.PI / 1.8),
      mat(config.hair),
    );
    hair.position.y = 1.35;
    hair.castShadow = true;
    this.root.add(hair);

    // Arms
    const armGeo = new THREE.CapsuleGeometry(0.08, 0.45, 4, 8);
    const leftArm = new THREE.Mesh(armGeo, bodyMat);
    leftArm.position.set(-0.35, 0.85, 0);
    leftArm.castShadow = true;
    this.root.add(leftArm);
    const rightArm = new THREE.Mesh(armGeo, bodyMat);
    rightArm.position.set(0.35, 0.85, 0);
    rightArm.castShadow = true;
    this.root.add(rightArm);

    // Legs
    const legGeo = new THREE.CapsuleGeometry(0.1, 0.4, 4, 8);
    const legMat = mat('#5a4a6a');
    const leftLeg = new THREE.Mesh(legGeo, legMat);
    leftLeg.position.set(-0.14, 0.25, 0);
    leftLeg.castShadow = true;
    this.root.add(leftLeg);
    const rightLeg = new THREE.Mesh(legGeo, legMat);
    rightLeg.position.set(0.14, 0.25, 0);
    rightLeg.castShadow = true;
    this.root.add(rightLeg);

    this.parts = { root: this.root, body, head, hair, leftArm, rightArm, leftLeg, rightLeg };

    this.applyType(config);
  }

  applyType(config: CharacterConfig): void {
    // Remove previous ears/wings
    if (this.parts.leftEar) { this.root.remove(this.parts.leftEar); this.parts.leftEar = undefined; }
    if (this.parts.rightEar) { this.root.remove(this.parts.rightEar); this.parts.rightEar = undefined; }
    if (this.parts.wings) { this.root.remove(this.parts.wings.group); this.parts.wings = undefined; }

    if (config.type === 'elf') {
      const earGeo = new THREE.ConeGeometry(0.07, 0.22, 8);
      const earMat = mat(config.skin);
      const lEar = new THREE.Mesh(earGeo, earMat);
      lEar.position.set(-0.28, 1.38, 0);
      lEar.rotation.z = Math.PI / 2.2;
      lEar.castShadow = true;
      const rEar = new THREE.Mesh(earGeo, earMat);
      rEar.position.set(0.28, 1.38, 0);
      rEar.rotation.z = -Math.PI / 2.2;
      rEar.castShadow = true;
      this.root.add(lEar, rEar);
      this.parts.leftEar = lEar;
      this.parts.rightEar = rEar;
    } else {
      const wingGroup = new THREE.Group();
      const wingMat = new THREE.MeshStandardMaterial({
        color: config.wings,
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide,
        roughness: 0.3,
        metalness: 0.1,
        emissive: config.wings,
        emissiveIntensity: 0.15,
      });
      const wingShape = new THREE.Shape();
      wingShape.moveTo(0, 0);
      wingShape.bezierCurveTo(0.1, 0.4, 0.6, 0.7, 0.8, 0.3);
      wingShape.bezierCurveTo(0.9, 0.0, 0.6, -0.3, 0.3, -0.15);
      wingShape.bezierCurveTo(0.15, -0.05, 0.05, -0.05, 0, 0);
      const wingGeo = new THREE.ShapeGeometry(wingShape);
      const left = new THREE.Mesh(wingGeo, wingMat);
      left.scale.set(-1, 1, 1);
      left.position.set(-0.05, 1.0, -0.15);
      const right = new THREE.Mesh(wingGeo, wingMat);
      right.position.set(0.05, 1.0, -0.15);
      wingGroup.add(left, right);
      this.root.add(wingGroup);
      this.parts.wings = { left, right, group: wingGroup };
    }
  }

  recolor(config: CharacterConfig): void {
    (this.parts.body.material as THREE.MeshStandardMaterial).color.set(config.outfit);
    (this.parts.leftArm.material as THREE.MeshStandardMaterial).color.set(config.outfit);
    (this.parts.rightArm.material as THREE.MeshStandardMaterial).color.set(config.outfit);
    (this.parts.head.material as THREE.MeshStandardMaterial).color.set(config.skin);
    (this.parts.hair.material as THREE.MeshStandardMaterial).color.set(config.hair);
    if (this.parts.leftEar) (this.parts.leftEar.material as THREE.MeshStandardMaterial).color.set(config.skin);
    if (this.parts.rightEar) (this.parts.rightEar.material as THREE.MeshStandardMaterial).color.set(config.skin);
    if (this.parts.wings) {
      const m = this.parts.wings.left.material as THREE.MeshStandardMaterial;
      m.color.set(config.wings);
      m.emissive.set(config.wings);
    }
  }

  animate(dt: number, moving: boolean, flying: boolean): void {
    if (flying) {
      this.flapTime += dt * 22;
      if (this.parts.wings) {
        const a = Math.sin(this.flapTime) * 0.6;
        this.parts.wings.left.rotation.y = a;
        this.parts.wings.right.rotation.y = -a;
      }
      // Tuck legs slightly
      this.parts.leftLeg.rotation.x = -0.3;
      this.parts.rightLeg.rotation.x = -0.3;
      this.parts.leftArm.rotation.x = 0.4;
      this.parts.rightArm.rotation.x = 0.4;
    } else if (moving) {
      this.walkTime += dt * 10;
      const a = Math.sin(this.walkTime) * 0.6;
      this.parts.leftLeg.rotation.x = a;
      this.parts.rightLeg.rotation.x = -a;
      this.parts.leftArm.rotation.x = -a * 0.6;
      this.parts.rightArm.rotation.x = a * 0.6;
      if (this.parts.wings) {
        this.flapTime += dt * 6;
        const w = Math.sin(this.flapTime) * 0.15;
        this.parts.wings.left.rotation.y = w;
        this.parts.wings.right.rotation.y = -w;
      }
    } else {
      // Idle easing back
      this.parts.leftLeg.rotation.x *= 0.85;
      this.parts.rightLeg.rotation.x *= 0.85;
      this.parts.leftArm.rotation.x *= 0.85;
      this.parts.rightArm.rotation.x *= 0.85;
      if (this.parts.wings) {
        this.flapTime += dt * 4;
        const w = Math.sin(this.flapTime) * 0.08;
        this.parts.wings.left.rotation.y = w;
        this.parts.wings.right.rotation.y = -w;
      }
    }
  }
}
